import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchApi } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { Card } from '@/components/Card'
import { Pencil, Trash2, Plus, X } from 'lucide-react'
import { formatDate } from '@/lib/date'

type UserRow = {
  id: number
  email: string
  name: string
  is_admin: number
  is_active: number
  created_at: string
  updated_at: string
}

type UserForm = {
  email: string
  name: string
  password: string
  is_admin: boolean
  is_active: boolean
}

const emptyForm: UserForm = { email: '', name: '', password: '', is_admin: false, is_active: true }

export function Users() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [users, setUsers] = useState<UserRow[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState<UserForm>(emptyForm)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard', { replace: true })
      return
    }
    loadUsers()
  }, [user, navigate])

  async function loadUsers() {
    setLoading(true)
    try {
      const data = await fetchApi<UserRow[]>('/users/')
      setUsers(data)
    } catch {
      // handled by api.ts 401 redirect
    } finally {
      setLoading(false)
    }
  }

  function openCreate() {
    setEditId(null)
    setForm(emptyForm)
    setError('')
    setShowModal(true)
  }

  function openEdit(u: UserRow) {
    setEditId(u.id)
    setForm({
      email: u.email,
      name: u.name,
      password: '',
      is_admin: !!u.is_admin,
      is_active: !!u.is_active,
    })
    setError('')
    setShowModal(true)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setSubmitting(true)

    try {
      if (editId) {
        const body: Record<string, unknown> = {
          email: form.email,
          name: form.name,
          is_admin: form.is_admin,
          is_active: form.is_active,
        }
        if (form.password) body.password = form.password
        await fetchApi(`/users/${editId}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        })
      } else {
        await fetchApi('/users/', {
          method: 'POST',
          body: JSON.stringify({
            email: form.email,
            name: form.name,
            password: form.password,
            is_admin: form.is_admin,
          }),
        })
      }
      setShowModal(false)
      await loadUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete(u: UserRow) {
    if (!confirm(`Delete user "${u.name}" (${u.email})?`)) return
    try {
      await fetchApi(`/users/${u.id}`, { method: 'DELETE' })
      await loadUsers()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete user')
    }
  }

  if (loading) {
    return <div className="p-6 text-[var(--color-text-muted)]">Loading...</div>
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[var(--color-text)]">User Management</h2>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-[var(--color-primary)] text-white text-sm hover:opacity-90 transition-opacity"
        >
          <Plus size={16} /> Add User
        </button>
      </div>

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)] text-[var(--color-text-muted)] text-left">
              <th className="py-2 px-3 font-medium">Name</th>
              <th className="py-2 px-3 font-medium">Email</th>
              <th className="py-2 px-3 font-medium">Role</th>
              <th className="py-2 px-3 font-medium">Status</th>
              <th className="py-2 px-3 font-medium">Created</th>
              <th className="py-2 px-3 font-medium w-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-[var(--color-border)] last:border-0">
                <td className="py-2 px-3 text-[var(--color-text)]">{u.name}</td>
                <td className="py-2 px-3 text-[var(--color-text-muted)]">{u.email}</td>
                <td className="py-2 px-3">
                  <span className={`text-xs px-2 py-0.5 rounded ${u.is_admin ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>
                    {u.is_admin ? 'Admin' : 'User'}
                  </span>
                </td>
                <td className="py-2 px-3">
                  <span className={`text-xs px-2 py-0.5 rounded ${u.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="py-2 px-3 text-[var(--color-text-muted)]">
                  {formatDate(u.created_at)}
                </td>
                <td className="py-2 px-3">
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEdit(u)}
                      title="Edit"
                      className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-hover)]"
                    >
                      <Pencil size={14} />
                    </button>
                    {u.id !== user?.id && (
                      <button
                        onClick={() => handleDelete(u)}
                        title="Delete"
                        className="p-1 rounded text-[var(--color-text-muted)] hover:text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-[var(--color-text)]">
                {editId ? 'Edit User' : 'Add User'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                <X size={18} />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-2 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm text-[var(--color-text-muted)] mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm focus:outline-none focus:border-[var(--color-primary)]"
                />
              </div>
              <div>
                <label className="block text-sm text-[var(--color-text-muted)] mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="w-full px-3 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm focus:outline-none focus:border-[var(--color-primary)]"
                />
              </div>
              <div>
                <label className="block text-sm text-[var(--color-text-muted)] mb-1">
                  Password{editId ? ' (leave blank to keep current)' : ''}
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required={!editId}
                  minLength={8}
                  className="w-full px-3 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm focus:outline-none focus:border-[var(--color-primary)]"
                />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-[var(--color-text)]">
                  <input
                    type="checkbox"
                    checked={form.is_admin}
                    onChange={(e) => setForm({ ...form, is_admin: e.target.checked })}
                    className="rounded"
                  />
                  Admin
                </label>
                {editId && (
                  <label className="flex items-center gap-2 text-sm text-[var(--color-text)]">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      className="rounded"
                    />
                    Active
                  </label>
                )}
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-2 rounded bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
                >
                  {submitting ? 'Saving...' : editId ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded border border-[var(--color-border)] text-[var(--color-text-muted)] text-sm hover:bg-[var(--color-bg-hover)] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
